import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const NotePage = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const { id: routeId } = useParams();
  const [noteId, setNoteId] = useState(routeId || '');
  const url = process.env.REACT_APP_API_URL || "https://codeforgesolution.com/SharedNotesApi/";
  const sUrl = "https://sharenotes.codeforgesolution.com/";
  // const url = "http://localhost:3001/";
  const shareUrl = `${sUrl}${noteId}`;

  // Auto-save to localStorage when changes are made
  useEffect(() => {
    if (!noteId) return;

    const autoSave = () => {
      try {
        localStorage.setItem('draft_note', JSON.stringify({ title, notes }));
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    };

    // Debounced auto-save every 5 seconds
    const autoSaveInterval = setInterval(autoSave, 5000);

    return () => clearInterval(autoSaveInterval);
  }, [noteId, title, notes]);

  // Load saved draft on page load
  useEffect(() => {
    if (!routeId) {
      return;
    }

    const loadNote = async () => {
      try {
        const response = await fetch(`${url}load-note/${routeId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setTitle(data.data.title || '');
          setNotes(data.data.notes || data.data.text || '');
          setNoteId(data.data._id || routeId);
        } else {
          // No note exists, load draft from localStorage if available
          const savedDraft = localStorage.getItem('draft_note');
          if (savedDraft) {
            try {
              const parsed = JSON.parse(savedDraft);
              setTitle(parsed.title || '');
              setNotes(parsed.notes || '');
            } catch (error) {
              console.error('Failed to load draft:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading note:', error);
      }
    };

    loadNote();
  }, [routeId, url]);

  const handleUnsave = () => {
    localStorage.removeItem('draft_note');
  };

  const handleSave = async () => {
    setSaveStatus('Saving...');

    const response = await fetch(`${url}add-note`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ title, notes, id: noteId }),
    });

    const data = await response.json();
    console.log(data);
    setNoteId(data.id);
    setSaveStatus('Saved');
  };

  const handleShare = () => {
    setIsShareOpen(true);
  };

  const handleDelete = async () => {
    setSaveStatus('Deleting...');

    try {
      const response = await fetch(`${url}delete-note/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSaveStatus('Deleted');
        setTimeout(() => window.location.reload(), 1000);
      } else {
        setSaveStatus('Failed to delete note');
      }
    } catch (error) {
      console.error(error);
      setSaveStatus('Error deleting note');
    }
  };

  const handleDeleteClick = () => {
    setNoteId(noteId);
    setDeleteConfirmOpen(true);
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {noteId ? 'Edit Note' : 'Create Note'}
      </Typography>

      <h1>{noteId ? `Editing` : 'Creating'}</h1>
      <Button
        variant="outlined"
        color="primary"
        sx={{ mt: 2, mb: 2 }}
        onClick={() => navigate('/dashboard')}
      >
        Go to Dashboard
      </Button>
      <TextField
        fullWidth
        label="Title"
        variant="outlined"
        sx={{ mb: 2 }}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <TextField
        fullWidth
        label="Text"
        variant="outlined"
        multiline
        minRows={8}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleSave}>Save</Button>
        <Button variant="outlined" onClick={handleShare}>Share</Button>
        {noteId && (
          <Button variant="outlined" color="error" onClick={handleDeleteClick}>
            Delete
          </Button>
        )}
      </Stack>

      {unsavedChanges && (
        <Typography
          variant="body1"
          sx={{ mt: 2, color: 'warning.main' }}
        >
          Unsaved changes detected. Click "Unsave" to clear local draft.
        </Typography>
      )}

      <Button
        size="small"
        variant="outlined"
        onClick={handleUnsave}
        sx={{ mt: 1, mb: 3 }}
      >
        Unsave
      </Button>

      {saveStatus && (
        <Typography sx={{ mt: 1, mb: 2 }}>{saveStatus}</Typography>
      )}

      <Dialog open={isShareOpen} onClose={() => setIsShareOpen(false)}>
        <DialogTitle>Share Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={shareUrl}
            InputProps={{ readOnly: true }}
            sx={{ mt: 1, minWidth: 350 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsShareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent sx={{ minWidth: 320, pl: 4 }}>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Are you sure you want to delete this note? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} autoFocus>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default NotePage;